import React, { useEffect, useState, useCallback } from "react";
import Map, { Marker, NavigationControl, Source, Layer } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import toast from "react-hot-toast";
import PlaceIcon from "@mui/icons-material/Place";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import { navigation } from "../../services/mapbox/navigationMap";
import CampaignIcon from "@mui/icons-material/Campaign";

const MapPreview = ({ coordinates = [], setLoading }) => {
  const [viewport, setViewport] = useState({
    latitude: 19.42847,
    longitude: -99.12766,
    zoom: 20,
  });
  const [completeRoute, setCompleteRoute] = useState(null);
  const mapRef = React.useRef();

  // Centrar el mapa en el primer punto de la ruta o en la ubicación del usuario
  const centerMap = useCallback(() => {
    if (!mapRef.current) return;
    // console.log("Complete Route:", completeRoute);

    // Si hay ruta, centrar en el primer punto
    if (
      Array.isArray(completeRoute) &&
      completeRoute.length >= 1 &&
      Array.isArray(completeRoute[0]) &&
      completeRoute[0].length >= 2
    ) {
      mapRef.current.flyTo({
        center: [completeRoute[0][0], completeRoute[0][1]],
        zoom: 17,
        duration: 1000,
      });
    }
    // Si no hay ruta pero sí ubicación del usuario
    else {
      mapRef.current.flyTo({
        center: [-99.12766, 19.42847],
        zoom: 14,
        duration: 1000,
      });
    }
  }, [completeRoute]);

  useEffect(() => {
    const getAllRoute = async () => {
      try {
        setLoading(true);
        const coordinatesString = coordinates
          .map((coord) => coord.join(","))
          .join(";");
        const responseMapbox = await navigation(coordinatesString);
        setCompleteRoute(responseMapbox?.geometry?.coordinates ?? null);
      } catch (error) {
        toast.error("No se pudo obtener la ruta. Inténtalo más tarde.");
      } finally {
        setLoading(false);
      }
    };

    if (coordinates.length >= 2) {
      getAllRoute();
    } else {
      setCompleteRoute(null);
    }
  }, [coordinates, setLoading]);

  // Ajustar la vista cuando cambian los datos
  useEffect(() => {
    centerMap();
  }, [completeRoute, centerMap]);

  const routeLine = {
    type: "Feature",
    properties: {},
    geometry: {
      type: "LineString",
      coordinates: completeRoute || [],
    },
  };

  return (
    <Map
      {...viewport}
      ref={mapRef}
      mapboxAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
      onMove={(evt) => setViewport(evt.viewState)}
      mapStyle="mapbox://styles/mapbox/streets-v11"
    >
      <NavigationControl position="top-right" />

      {/* Línea de la ruta */}
      {completeRoute && completeRoute.length >= 2 && (
        <Source id="route" type="geojson" data={routeLine}>
          <Layer
            id="route"
            type="line"
            source="route"
            layout={{
              "line-join": "round",
              "line-cap": "round",
            }}
            paint={{
              "line-color": "black",
              "line-width": 10,
              "line-opacity": 0.8,
            }}
          />
        </Source>
      )}
      {/* Incidencias */}
      {completeRoute &&
        completeRoute.icidents >= 0 &&
        completeRoute.icidents.map((coord, index) => (
          <Marker
            key={`marker-${index}`}
            longitude={coord[0]}
            latitude={coord[1]}
          >
            <div
              style={{
                position: "relative",
                transform: "translateY(-50%)",
                filter: "drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.9))",
              }}
            >
              <CampaignIcon
                style={{
                  color: "red",
                  fontSize: 40,
                  transform: "scale(1.2)",
                }}
              />
            </div>
          </Marker>
        ))}
      {/* Marcadores */}
      {completeRoute &&
        completeRoute
          .filter(
            (_, index) => index === 0 || index === completeRoute.length - 1
          )
          .map((coord, index) => (
            <Marker
              key={`marker-${index}`}
              longitude={coord[0]}
              latitude={coord[1]}
            >
              <div
                style={{
                  position: "relative",
                  transform: "translateY(-50%)",
                  filter: "drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.9))",
                }}
              >
                {index === 1 && ( // como ya filtraste solo hay 2 elementos
                  <PlaceIcon
                    style={{
                      color: "red",
                      fontSize: 40,
                      transform: "scale(1.2)",
                    }}
                  />
                )}
                {index === 0 && (
                  <DirectionsRunIcon
                    style={{
                      color: "red",
                      fontSize: 40,
                      transform: "scale(1.2)",
                    }}
                  />
                )}
              </div>
            </Marker>
          ))}
    </Map>
  );
};

export default MapPreview;
